import React, { useEffect, useState } from "react";
import { Tag as TagIcon, Check } from "lucide-react";


import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import { Avatar, AvatarImage, AvatarFallback, AvatarPreview } from "../ui/avatar";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { canalDoTicket } from "../../helpers/canalDoTicket.jsx";
import { esperaDoTicket } from "../../helpers/tempoDeEspera";

// Quem está com o atendimento, em português. Duas linhas bastam: o nome do
// contato e uma frase curta — nada de rótulos técnicos para decifrar.
const TicketInfo = ({ contact, ticket, onClick }) => {
  const initials = (contact?.name || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const canal = canalDoTicket(ticket);
  const CanalIcone = canal.icone;
  const espera = esperaDoTicket(ticket);

  return (
    <button
      type="button"
      onClick={onClick}
      title="Ver dados do contato"
      className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1 pl-1 pr-2.5 text-left transition-colors hover:bg-accent"
    >
      <span className="relative shrink-0">
        <AvatarPreview
          className="h-10 w-10"
          previewUrl={contact?.profilePicUrl}
          alt={contact?.name}
        >
          <AvatarImage src={contact?.profilePicUrl} alt={contact?.name} />
          <AvatarFallback>{initials}</AvatarFallback>
        </AvatarPreview>
        <span
          title={canal.rotulo}
          aria-label={`Canal: ${canal.rotulo}`}
          className={cn(
            "absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-background",
            canal.classe,
          )}
        >
          <CanalIcone className="h-2.5 w-2.5" />
        </span>
      </span>

      <div className="min-w-0">
        <p className="flex min-w-0 items-baseline gap-1.5 truncate text-[15px] font-semibold leading-tight">
          <span className="truncate">{contact?.name}</span>
          <span className="shrink-0 font-normal text-muted-foreground">
            #{ticket.id}
          </span>
        </p>

        <p className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground">
          {/* O nome da rede por extenso, não só o selo no avatar: com
              Instagram, Telegram, Messenger e X na mesma lista, um ícone de
              4px não diz para onde a resposta vai sair. */}
          <span
            className={cn(
              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
              canal.classePill,
            )}
          >
            {canal.rotulo}
          </span>

          {ticket.user?.name ? (
            <span className="truncate">Atribuído à: {ticket.user.name}</span>
          ) : (
            <span className="truncate">Ninguém assumiu ainda</span>
          )}

          {/* A espera é o único ponto de cor da linha: é o que aperta. */}
          {espera && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <span className={cn("shrink-0 font-medium", espera.classe)}>
                {ticket.status === "pending"
                  ? "aguardando há "
                  : "sem resposta há "}
                {espera.rotulo}
              </span>
            </>
          )}
        </p>
      </div>
    </button>
  );
};

// Etiquetas do atendimento: um ícone com a quantidade, e o resto só ao abrir.
//
// Antes era uma faixa fixa abaixo do cabeçalho, com os nomes das etiquetas
// sempre à mostra — comia uma linha inteira da conversa para uma informação
// que quase nunca muda. Agora é ícone; quem quer ver ou mexer, clica.
export const TicketTagsBar = ({ ticket }) => {
  const [aberto, setAberto] = useState(false);
  const [todas, setTodas] = useState([]);
  // Estado local para o clique responder na hora; o servidor confirma depois.
  const [marcadas, setMarcadas] = useState(() => new Set());

  const etiquetas = ticket.tags || [];

  useEffect(() => {
    setMarcadas(new Set(etiquetas.map((t) => t.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.tags]);

  // A lista completa só é buscada quando o modal abre — é o único momento em
  // que ela serve, e o ícone aparece em toda conversa.
  useEffect(() => {
    if (!aberto) return;

    (async () => {
      try {
        const { data } = await api.get("/tags");
        setTodas(data?.tags || data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [aberto]);

  const alternar = async (tagId) => {
    const novo = new Set(marcadas);

    if (novo.has(tagId)) novo.delete(tagId);
    else novo.add(tagId);

    setMarcadas(novo);

    try {
      await api.put(`/tickets/${ticket.id}/tags`, { tagIds: [...novo] });
    } catch (err) {
      // Desfaz o que a tela já mostrou: senão fica dizendo que aplicou algo
      // que o servidor recusou.
      setMarcadas(marcadas);
      toastError(err);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        // Mesmo formato do "Arquivos do atendimento" e das notas, ao lado dos
        // quais ele agora fica: na fila de ações do cabeçalho, e não mais
        // flutuando por cima da foto do contato.
        className="relative shrink-0"
        title={
          etiquetas.length
            ? `Etiquetas: ${etiquetas.map((t) => t.name).join(", ")}`
            : "Sem etiquetas"
        }
        aria-label="Etiquetas do atendimento"
        onClick={() => setAberto(true)}
      >
        <TagIcon className="h-5 w-5" />
        {etiquetas.length > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {etiquetas.length}
          </span>
        )}
      </Button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Etiquetas</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {marcadas.size
                ? `${marcadas.size} aplicada${marcadas.size > 1 ? "s" : ""} neste atendimento`
                : "Nenhuma aplicada neste atendimento"}
            </p>
          </DialogHeader>

          {/* Lista própria em vez do seletor genérico: com a cor à esquerda e
              o certo à direita, dá para ver o que está aplicado sem ler duas
              vezes a mesma lista — que era o que deixava isto confuso. */}
          <div className="-mx-1 max-h-72 space-y-0.5 overflow-y-auto px-1">
            {todas.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nenhuma etiqueta cadastrada ainda.
              </p>
            )}

            {todas.map((tag) => {
              const ativa = marcadas.has(tag.id);

              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => alternar(tag.id)}
                  aria-pressed={ativa}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                    ativa ? "bg-accent" : "hover:bg-accent/60",
                  )}
                >
                  <span
                    className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                  {ativa && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            Vale na hora: não há botão de salvar.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketInfo;
